import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ThemeToggle } from '../components/landing/ThemeToggle';
import { usePublicTheme } from '../hooks/usePublicTheme';

export default function PublicSignup() {
  const { listSlug } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [defaultTheme, setDefaultTheme] = useState(null);
  const { theme: mode, toggle } = usePublicTheme(defaultTheme);

  useEffect(() => {
    api.get(`/public/signup/${listSlug}`).then((r) => setInfo(r.data)).catch(() => setInfo({}));
    api.get('/public/landing').then((r) => setDefaultTheme(r.data?.default_theme || 'light')).catch(() => setDefaultTheme('light'));
  }, [listSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/signup/${listSlug}`, form);
      setDone(true);
    } catch (err) { toast.error('Could not sign up'); }
    finally { setSubmitting(false); }
  };

  const theme = info?.theme_color || '#2F6B57';
  const accent = info?.accent_color || '#C86B4A';

  return (
    <div className="tg-public min-h-screen grid lg:grid-cols-2 bg-background text-foreground" data-theme={mode} data-testid="public-signup-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle theme={mode} onToggle={toggle} />
      </div>
      <div className="hidden lg:flex relative tg-noise flex-col justify-between p-12 text-white" style={{ background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
        <div className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center"><Sparkles className="h-5 w-5" /></div><span className="font-display text-xl font-semibold">Thrive Gatherings</span></div>
        <div><h2 className="font-display text-4xl font-semibold leading-tight">Never miss<br/>a gathering.</h2><p className="mt-4 text-white/80 max-w-md">{info?.host ? `Join ${info.host}'s list` : 'Join our list'} and be the first to know about upcoming events.</p></div>
        <span className="text-sm text-white/60">thrivegathering.life</span>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          {done ? (
            <div className="text-center" data-testid="signup-success">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-primary" /></div>
              <h1 className="font-display text-3xl font-semibold">You&apos;re on the list!</h1>
              <p className="text-muted-foreground mt-2">Thanks for joining. We&apos;ll keep you posted on the next gathering.</p>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: theme + '15' }}><Mail className="h-6 w-6" style={{ color: theme }} /></div>
              <h1 className="font-display text-3xl font-semibold">Join the email list</h1>
              <p className="mt-1.5 text-sm text-muted-foreground">{info?.event_title ? `Stay in the loop after ${info.event_title}.` : 'Get notified about upcoming gatherings.'}</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
                <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="signup-name-input" /></div>
                <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="signup-email-input" /></div>
                <Button type="submit" disabled={submitting} className="w-full h-11 gap-2" style={{ background: theme }} data-testid="signup-submit-button">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{submitting ? 'Joining…' : 'Join the list'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
